/**
 * Parts of an Expression generator (6.PAFR.2.1).
 *
 * Builds a small algebraic expression (e.g. 3x + 5 or 4y - 2 + y) and asks a
 * multiple-choice question about one of its parts: a coefficient, the constant,
 * the number of terms, or which piece is the variable term. The expression is
 * built from Term objects, and the correct answer plus distractors are derived
 * from `identifyParts`, so the graded answer always matches the shown
 * expression.
 *
 * Families:
 *   Q1: coefficient of the variable term
 *   Q2: constant term
 *   Q3: number of terms
 *   Q4: identify the variable term
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import {
  constant,
  varTerm,
  termsToLatex,
  termsToText,
  identifyParts,
  type Term,
} from "../engine/expression";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "parts-expression" as const;
const VERSION = "1.0.0";

type Family = "Q1" | "Q2" | "Q3" | "Q4";

interface Cfg {
  readonly families: readonly Family[];
  readonly coeffMax: number;
  readonly constMax: number;
  /** Whether a second variable term (combinable) may appear. */
  readonly allowTwoVarTerms: boolean;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["Q1", "Q2"], coeffMax: 9, constMax: 9, allowTwoVarTerms: false },
  standard: { families: ["Q1", "Q2", "Q3", "Q4"], coeffMax: 12, constMax: 12, allowTwoVarTerms: false },
  hard: { families: ["Q1", "Q2", "Q3", "Q4"], coeffMax: 15, constMax: 15, allowTwoVarTerms: true },
  challenge: { families: ["Q3", "Q4"], coeffMax: 20, constMax: 20, allowTwoVarTerms: true },
};

const VARS = ["x", "y", "n", "a"] as const;

export class PartsExpressionGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const v = rng.pick(VARS);

      const coeff = rat(rng.int(2, cfg.coeffMax));
      const konst = rat(rng.int(1, cfg.constMax));
      const terms: Term[] = [varTerm(coeff, v), constant(konst)];

      // Optionally add a second variable term for a richer expression.
      let secondCoeff: Rational | null = null;
      if (cfg.allowTwoVarTerms && rng.bool(0.5)) {
        secondCoeff = rat(rng.int(1, cfg.coeffMax));
        terms.push(varTerm(secondCoeff, v));
      }

      const exprLatex = termsToLatex(terms);
      const exprText = termsToText(terms);
      const parts = identifyParts(terms);

      let promptText: string;
      let choices: Choice[];
      let correctId: string;
      let explanation: string;

      switch (family) {
        case "Q1": {
          // Coefficient of the (first) variable term.
          const correct = coeff;
          promptText = `In the expression ${exprText}, what is the coefficient of the first ${v} term?`;
          const distractors = [konst, coeff.add(rat(1)), rat(1)];
          ({ choices, correctId } = buildNumberChoices(rng, correct, distractors));
          explanation = `The coefficient is the number multiplying the variable, so ${correct.toString()}.`;
          break;
        }
        case "Q2": {
          const correct = konst;
          promptText = `In the expression ${exprText}, what is the constant term?`;
          const distractors = [coeff, konst.add(rat(1)), secondCoeff ?? konst.add(rat(2))];
          ({ choices, correctId } = buildNumberChoices(rng, correct, distractors));
          explanation = `The constant is the term with no variable, so ${correct.toString()}.`;
          break;
        }
        case "Q3": {
          const correct = rat(parts.terms);
          promptText = `How many terms are in the expression ${exprText}?`;
          const distractors = [rat(parts.terms + 1), rat(parts.terms - 1), rat(parts.terms + 2)];
          ({ choices, correctId } = buildNumberChoices(rng, correct, distractors));
          explanation = `Terms are the pieces separated by + or -, so there are ${parts.terms}.`;
          break;
        }
        case "Q4": {
          // Which piece is a variable term (as opposed to the constant)?
          const varLatex = termsToLatex([varTerm(coeff, v)]);
          const options: { id: string; latex: string; correct: boolean }[] = [
            { id: "var1", latex: varLatex, correct: true },
            { id: "const", latex: konst.toLatex(), correct: false },
            { id: "num", latex: coeff.toLatex(), correct: false },
          ];
          if (secondCoeff) {
            options.push({ id: "var2", latex: termsToLatex([varTerm(secondCoeff, v)]), correct: false });
          }
          const shuffled = rng.shuffle(options);
          choices = shuffled.map((o) => ({ id: o.id, latex: o.latex }));
          correctId = "var1";
          promptText = `In ${exprText}, which of these is a variable term?`;
          explanation = `A variable term contains the variable ${v}, so ${varLatex.replace(/\\/g, "")}.`;
          break;
        }
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: exprLatex },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [{ description: explanation, latex: exprLatex }],
        hints: [
          { level: 1, text: "A term is a piece separated by + or -." },
          {
            level: 2,
            text: "A coefficient multiplies a variable; a constant stands alone with no variable.",
          },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

/**
 * Build number-valued multiple-choice options: the correct value plus unique
 * distractors, shuffled. Returns the choice list and the id of the correct one.
 */
function buildNumberChoices(
  rng: Rng,
  correct: Rational,
  rawDistractors: readonly Rational[],
): { choices: Choice[]; correctId: string } {
  const seen = new Set<string>([correct.key()]);
  const options: { id: string; value: Rational; correct: boolean }[] = [
    { id: "c0", value: correct, correct: true },
  ];
  let n = 1;
  for (const d of rawDistractors) {
    if (options.length >= 4) break;
    if (d.isZero() && correct.isZero()) continue;
    if (seen.has(d.key())) continue;
    seen.add(d.key());
    options.push({ id: `c${n++}`, value: d, correct: false });
  }
  const shuffled = rng.shuffle(options);
  const choices: Choice[] = shuffled.map((o) => ({ id: o.id, latex: o.value.toLatex() }));
  const correctId = (shuffled.find((o) => o.correct) as { id: string }).id;
  return { choices, correctId };
}

export const partsExpression = new PartsExpressionGenerator();
